import {
  ESdChangeReason,
  IAuthorizationDto,
  IDisplayItem,
  IProjectRole,
  IRoleDto,
  IScreenDesignInfo,
  SpecialDIIds
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import { IScreenDesignContextState } from "../../contexts/screen-design-context/screen-design-context";
import type { ScreenDesignInstance } from "./designer-context";
import { getValues } from "./designer-context";
import { getDisplayItem, getScreenDesign, setScreenDesign } from "./designer-utils";

interface IRoleSummary {
  id: string;
  name: string;
  startingScreenId?: string | null;
}

interface IListRolesResult {
  roles: IRoleSummary[];
  count: number;
  message?: string;
}

interface IRoleMutationSuccess {
  success: true;
  message: string;
  role?: IRoleSummary | { name: string };
}

interface IRoleMutationError {
  error: string;
  availableRoles?: Array<{ id: string; name: string }>;
}

interface IDeleteRoleSuccess {
  success: true;
  message: string;
}

interface IScreenAuthorizationResult {
  screenName: string | null;
  targetType: "screen" | "element";
  targetName: string | null;
  targetKuikaId: string | null;
  allowAnonymousAccess: boolean;
  allowAllRoles: boolean;
  isHidden: boolean;
  isDisabled: boolean;
  permittedRoles: Array<{ id: string; name: string }>;
  permittedRoleCount: number;
}

interface ISetAuthorizationSuccess {
  success: true;
  message: string;
}

interface ISetScreenAuthorizationInput {
  allowAnonymousAccess?: boolean;
  allowAllRoles?: boolean;
  isHidden?: boolean;
  isDisabled?: boolean;
  roleIds?: string[];
}

interface IAuthorizationTarget {
  targetType: "screen" | "element";
  targetName: string | null;
  targetKuikaId: string | null;
  authorization: IAuthorizationDto;
}

function getRoles(sd: IScreenDesignContextState): IProjectRole[] {
  return sd?.project?.roles || [];
}

/**
 * A role's `startingScreenId` is a REFERENCE to a project screen, so it is only
 * valid if it names a screen that actually exists. The LLM driving the MCP tools
 * fills unused flat-schema parameters with placeholder junk ("x", "ignore", …),
 * and create_role/update_role used to persist that verbatim — leaving the role
 * pointed at a non-existent screen, which breaks code generation (KOA-17362).
 * Resolve the candidate against the real screen list (same lookup as
 * `designer-screen-actions.getScreenInfo`) and keep it ONLY when it resolves;
 * otherwise drop to `undefined`. A role with no starting screen is always valid
 * — it falls back to the project default. This is reference validation, not a
 * placeholder blocklist: any id absent from the project (typo, stale, junk) is
 * dropped the same way, so the fix is convention- and locale-agnostic.
 */
function resolveStartingScreenId(
  sd: IScreenDesignContextState,
  candidate: string | null | undefined
): string | undefined {
  if (candidate == null || candidate === "") return undefined;
  const match = (sd?.screenList || []).find((screen: IScreenDesignInfo) => String(screen.id) === String(candidate));
  return match ? String(match.id) : undefined;
}

function getDefaultAuthorization(): IAuthorizationDto {
  return {
    permittedRoles: [],
    hide: false,
    disable: false,
    allowAnonymousAccess: false,
    allowAllRoles: true
  } as IAuthorizationDto;
}

function ensureMutableAuthorization(authorization?: IAuthorizationDto): IAuthorizationDto {
  const resolvedAuthorization = authorization || getDefaultAuthorization();
  if (!resolvedAuthorization.permittedRoles) {
    resolvedAuthorization.permittedRoles = [];
  }
  return resolvedAuthorization;
}

function getSelectedDisplayItemAuthorizationTarget(sd: IScreenDesignContextState): IDisplayItem | undefined {
  const screenDesign = getScreenDesign(sd);
  if (typeof sd?.getSelectedDisplayItem === "function") {
    const selectedDisplayItem = sd.getSelectedDisplayItem(screenDesign);
    if (selectedDisplayItem) return selectedDisplayItem;
  }

  const selectedKuikaId = sd?.selectedKuikaIds?.[0];
  if (!selectedKuikaId || selectedKuikaId === SpecialDIIds.rootDI.toString()) return undefined;
  return getDisplayItem(sd, selectedKuikaId);
}

function getAuthorizationTarget(sd: IScreenDesignContextState): IAuthorizationTarget {
  const screenDesign = getScreenDesign(sd);
  const selectedKuikaId = sd?.selectedKuikaIds?.[0] || null;
  const selectedDisplayItem = getSelectedDisplayItemAuthorizationTarget(sd);

  if (selectedDisplayItem && selectedKuikaId && selectedKuikaId !== SpecialDIIds.rootDI.toString()) {
    selectedDisplayItem.authorization = ensureMutableAuthorization(selectedDisplayItem.authorization);
    return {
      targetType: "element",
      targetName:
        selectedDisplayItem.friendlyName || selectedDisplayItem.item?.name || selectedDisplayItem.item?.id || null,
      targetKuikaId: selectedKuikaId,
      authorization: selectedDisplayItem.authorization
    };
  }

  screenDesign.authorization = ensureMutableAuthorization(screenDesign.authorization);
  return {
    targetType: "screen",
    targetName: screenDesign.name || null,
    targetKuikaId: SpecialDIIds.rootDI.toString(),
    authorization: screenDesign.authorization
  };
}

function getAuthorizationMessageScope(target: Pick<IAuthorizationTarget, "targetType">): string {
  return target.targetType === "element" ? "selected element authorization" : "screen authorization";
}

function getAuthorizationTargetSummary(
  target: Pick<IAuthorizationTarget, "targetType" | "targetName" | "targetKuikaId">
): string {
  if (target.targetType !== "element") return "current screen";
  const name = target.targetName || "selected element";
  return target.targetKuikaId ? `${name} (${target.targetKuikaId})` : name;
}

async function updateRole(sd: IScreenDesignContextState, payload: IProjectRole): Promise<void> {
  // The provider answers with the saved role's id; the caller here only needs the write to land.
  await (sd?.updateRole?.(payload) ?? Promise.resolve());
}

function deleteRole(sd: IScreenDesignContextState, roleId: string): Promise<void> {
  return sd?.deleteRole?.(roleId) ?? Promise.resolve();
}

/**
 * KOA-18448 — ONE ROLE WRITE AT A TIME.
 *
 * ## What went wrong without it
 *
 * Live session `2f2a631b`: the assistant created three roles in a single turn — all three
 * answered `success`, each returning the id it had just been given. Seconds later a fresh read
 * of the project's roles showed only two of them. The third was gone, so the process lane that
 * wanted it could not be assigned, and the retry — finding no role by that name — created a
 * SECOND role with the same name, which the duplicate check above should have made impossible.
 * The project ended with a duplicate role and a workflow step that reaches nobody.
 *
 * ## Why a queue is the fix rather than a bigger check
 *
 * Every one of these mutations is read-modify-write on the shared project, and the write itself
 * is a diff against a baseline that only moves forward once its own network response lands. Two
 * writes started in the same tick therefore both read the same "before", and both report success
 * before either has settled. The duplicate check cannot help: it runs against a list whose most
 * recent member has not been persisted yet.
 *
 * Serialising makes the read and the write ONE step — each mutation observes every mutation
 * before it, which is the property the duplicate check was written assuming it already had.
 *
 * A rejected write must not wedge the queue, so the chain continues on failure and each caller
 * still receives its own outcome.
 */
let roleWriteQueue: Promise<unknown> = Promise.resolve();

function queueRoleWrite<T>(work: () => Promise<T>): Promise<T> {
  const run: Promise<T> = roleWriteQueue.then(work, work);
  roleWriteQueue = run.catch((): undefined => undefined);
  return run;
}

function toggleAuthorizedRole(sd: IScreenDesignContextState, role: IRoleDto): void {
  sd?.updateScreenAuthorization?.(role);
}

function toggleAllowAllRoles(sd: IScreenDesignContextState): void {
  sd?.allowAllRolesChanged?.();
}

function toggleAllowAnonymousAccess(sd: IScreenDesignContextState): void {
  sd?.allowAnonymousAccessChanged?.();
}

function toggleIsHidden(sd: IScreenDesignContextState): void {
  sd?.isHiddenChanged?.();
}

function toggleIsDisabled(sd: IScreenDesignContextState): void {
  sd?.isDisabledChanged?.();
}

function syncPermittedRoles(
  sd: IScreenDesignContextState,
  auth: IAuthorizationDto,
  roleIds: string[]
): { added: string[]; removed: string[]; invalid: string[] } {
  const roles = getRoles(sd);
  const current = auth.permittedRoles || [];
  const validIds = roleIds.filter((id: string) => roles.some((role: IProjectRole) => role.id === id));
  const invalidIds = roleIds.filter((id: string) => !roles.some((role: IProjectRole) => role.id === id));
  const toAdd = validIds.filter((id: string) => !current.includes(id));
  const toRemove = current.filter((id: string) => !validIds.includes(id));

  if (typeof sd?.updateScreenAuthorization === "function") {
    toAdd.forEach((id: string) => {
      const role = roles.find((item: IProjectRole) => item.id === id);
      if (role) toggleAuthorizedRole(sd, role as unknown as IRoleDto);
    });
    toRemove.forEach((id: string) => {
      const role = roles.find((item: IProjectRole) => item.id === id);
      if (role) toggleAuthorizedRole(sd, role as unknown as IRoleDto);
    });
  } else {
    auth.permittedRoles = validIds;
    setScreenDesign(sd, getScreenDesign(sd), ESdChangeReason.UpdateAuth);
  }

  return { added: toAdd, removed: toRemove, invalid: invalidIds };
}

export function listDesignerRoles(sdInstance: ScreenDesignInstance): IListRolesResult {
  const sd = getValues(sdInstance);
  const roles = getRoles(sd);

  if (roles.length === 0) {
    return { roles: [], count: 0, message: "No roles found in this project." };
  }

  return {
    roles: roles.map((role: IProjectRole) => ({
      id: role.id,
      name: role.name,
      startingScreenId: role.startingScreenId || null
    })),
    count: roles.length
  };
}

export function createDesignerRole(
  sdInstance: ScreenDesignInstance,
  input: { name: string; startingScreenId?: string }
): Promise<IRoleMutationSuccess | IRoleMutationError> {
  const name = input.name?.trim();
  if (!name) return Promise.resolve({ error: "name is required" });

  // The duplicate check and the write are ONE queued step: reading the role list outside the
  // queue is what let two roles of the same name be created in one turn (see `queueRoleWrite`).
  return queueRoleWrite(async (): Promise<IRoleMutationSuccess | IRoleMutationError> => {
    const sd = getValues(sdInstance);
    const roles = getRoles(sd);
    const duplicate = roles.find((role: IProjectRole) => role.name?.toLowerCase() === name.toLowerCase());
    if (duplicate) {
      return { error: `A role with name "${name}" already exists (id: ${duplicate.id}).` };
    }

    const payload: Partial<IProjectRole> & { name: string } = { name };
    const resolvedStartingScreenId = resolveStartingScreenId(sd, input.startingScreenId);
    if (resolvedStartingScreenId) {
      payload.startingScreenId = resolvedStartingScreenId;
    }

    // Awaited: until this settles the role exists only in this tab, and "created" would be a
    // claim about a write still in flight.
    await updateRole(sd, payload as IProjectRole);

    // Trigger auth panel re-render
    const screenDesign = getScreenDesign(sd);
    setScreenDesign(sd, screenDesign, ESdChangeReason.UpdateAuth);

    const created = getRoles(sd).find((role: IProjectRole) => role.name === name);
    return {
      success: true,
      message: `Created role "${name}"`,
      role: created
        ? { id: created.id, name: created.name, startingScreenId: created.startingScreenId || null }
        : { name }
    };
  });
}

export function updateDesignerRole(
  sdInstance: ScreenDesignInstance,
  input: { roleId: string; name?: string; startingScreenId?: string }
): Promise<IRoleMutationSuccess | IRoleMutationError> {
  const { roleId, startingScreenId } = input;
  if (!roleId) return Promise.resolve({ error: "roleId is required" });

  // Same queue as create: a rename reads the role list to check for a clash, so it has to
  // observe every write before it (see `queueRoleWrite`).
  return queueRoleWrite(async (): Promise<IRoleMutationSuccess | IRoleMutationError> => {
    const sd = getValues(sdInstance);
    const roles = getRoles(sd);
    const existing = roles.find((role: IProjectRole) => role.id === roleId);
    if (!existing) {
      return {
        error: `Role with id "${roleId}" not found.`,
        availableRoles: roles.map((role: IProjectRole) => ({ id: role.id, name: role.name }))
      };
    }

    const name = input.name?.trim() || existing.name;
    const duplicate = roles.find(
      (role: IProjectRole) => role.id !== roleId && role.name?.toLowerCase() === name.toLowerCase()
    );
    if (duplicate) {
      return { error: `Another role with name "${name}" already exists (id: ${duplicate.id}).` };
    }

    // Only overwrite the starting screen with a value that resolves to a real
    // screen; an unresolvable candidate (placeholder junk / stale id) keeps the
    // existing reference rather than corrupting it.
    const resolvedStartingScreenId = resolveStartingScreenId(sd, startingScreenId);
    await updateRole(sd, {
      id: roleId,
      name,
      startingScreenId: resolvedStartingScreenId !== undefined ? resolvedStartingScreenId : existing.startingScreenId
    } as IProjectRole);

    // Trigger auth panel re-render
    const screenDesign = getScreenDesign(sd);
    setScreenDesign(sd, screenDesign, ESdChangeReason.UpdateAuth);

    return {
      success: true,
      message: `Updated role "${name}"`
    };
  });
}

export function deleteDesignerRole(
  sdInstance: ScreenDesignInstance,
  roleId: string
): Promise<IDeleteRoleSuccess | IRoleMutationError> {
  if (!roleId) return Promise.resolve({ error: "roleId is required" });

  // Same queue as create/update: a delete that overlaps a create would diff against a baseline
  // that never saw the created role (see `queueRoleWrite`).
  return queueRoleWrite(async (): Promise<IDeleteRoleSuccess | IRoleMutationError> => {
    const sd = getValues(sdInstance);
    const roles = getRoles(sd);
    const existing = roles.find((role: IProjectRole) => role.id === roleId);
    if (!existing) {
      return {
        error: `Role with id "${roleId}" not found.`,
        availableRoles: roles.map((role: IProjectRole) => ({ id: role.id, name: role.name }))
      };
    }

    await deleteRole(sd, roleId);

    const screenDesign = getScreenDesign(sd);
    const target = getAuthorizationTarget(sd);
    let cleanedCurrentTarget = false;
    const permittedRoles = target.authorization?.permittedRoles;
    if (Array.isArray(permittedRoles)) {
      const index = permittedRoles.indexOf(roleId);
      if (index !== -1) {
        permittedRoles.splice(index, 1);
        cleanedCurrentTarget = true;
      }
    }

    // Trigger auth panel re-render
    setScreenDesign(sd, screenDesign, ESdChangeReason.UpdateAuth);

    const message = `Deleted role "${existing.name}" (id: ${roleId})`;
    return {
      success: true,
      message: cleanedCurrentTarget
        ? `${message}. Also removed from ${getAuthorizationTargetSummary(target)} permitted roles.`
        : message
    };
  });
}

export function getDesignerScreenAuthorization(sdInstance: ScreenDesignInstance): IScreenAuthorizationResult {
  const sd = getValues(sdInstance);
  const target = getAuthorizationTarget(sd);
  const auth = target.authorization;
  const roles = getRoles(sd);
  const roleIdSet = new Set(roles.map((role: IProjectRole) => role.id));

  const permittedRoles = (auth.permittedRoles || [])
    .filter((roleId: string) => roleIdSet.has(roleId))
    .map((roleId: string) => ({
      id: roleId,
      name: roles.find((role: IProjectRole) => role.id === roleId)!.name
    }));

  const screenDesign = getScreenDesign(sd);
  return {
    screenName: screenDesign.name || null,
    targetType: target.targetType,
    targetName: target.targetName,
    targetKuikaId: target.targetKuikaId,
    allowAnonymousAccess: auth.allowAnonymousAccess ?? false,
    allowAllRoles: auth.allowAllRoles ?? true,
    isHidden: auth.hide ?? false,
    isDisabled: auth.disable ?? false,
    permittedRoles,
    permittedRoleCount: permittedRoles.length
  };
}

function normalizeScreenAuthorizationInput(
  auth: IAuthorizationDto,
  input: ISetScreenAuthorizationInput
): ISetScreenAuthorizationInput {
  const normalizedInput = { ...input };
  const currentRoleIds = auth.permittedRoles || [];
  const hasExplicitRoleSelection =
    Array.isArray(normalizedInput.roleIds) && (normalizedInput.roleIds.length > 0 || currentRoleIds.length > 0);

  if (normalizedInput.allowAnonymousAccess === true) {
    normalizedInput.allowAllRoles = true;
    if (normalizedInput.roleIds !== undefined || currentRoleIds.length > 0) {
      normalizedInput.roleIds = [];
    }
    normalizedInput.isHidden = false;
    normalizedInput.isDisabled = false;
    return normalizedInput;
  }

  if (normalizedInput.allowAllRoles === true) {
    normalizedInput.allowAnonymousAccess = false;
    if (normalizedInput.roleIds !== undefined || currentRoleIds.length > 0) {
      normalizedInput.roleIds = [];
    }
    normalizedInput.isHidden = false;
    normalizedInput.isDisabled = false;
    return normalizedInput;
  }

  if (hasExplicitRoleSelection || normalizedInput.allowAllRoles === false) {
    // Mirrors the Authorization panel flow: Restricted tab -> Everyone off -> Roles selectbox sync.
    if (normalizedInput.allowAnonymousAccess === undefined) {
      normalizedInput.allowAnonymousAccess = false;
    }
    if (normalizedInput.allowAllRoles === undefined) {
      normalizedInput.allowAllRoles = false;
    }

    const hasDefaultUnauthorizedBehaviorScaffold =
      (normalizedInput.isHidden === undefined && normalizedInput.isDisabled === undefined) ||
      (normalizedInput.isHidden === false && normalizedInput.isDisabled === false);

    // The panel defaults unauthorized behavior to Hide unless the caller explicitly asks for Disable.
    if (hasDefaultUnauthorizedBehaviorScaffold) {
      normalizedInput.isHidden = true;
      normalizedInput.isDisabled = false;
    }
  }

  return normalizedInput;
}

export function setDesignerScreenAuthorization(
  sdInstance: ScreenDesignInstance,
  input: ISetScreenAuthorizationInput
): ISetAuthorizationSuccess {
  const sd = getValues(sdInstance);
  const changes: string[] = [];
  const target = getAuthorizationTarget(sd);
  const auth = target.authorization;
  const normalizedInput = normalizeScreenAuthorizationInput(auth, input);

  if (
    normalizedInput.allowAnonymousAccess !== undefined &&
    auth.allowAnonymousAccess !== normalizedInput.allowAnonymousAccess
  ) {
    if (typeof sd?.allowAnonymousAccessChanged === "function") {
      toggleAllowAnonymousAccess(sd);
    } else {
      auth.allowAnonymousAccess = normalizedInput.allowAnonymousAccess;
      if (normalizedInput.allowAnonymousAccess) {
        auth.allowAllRoles = true;
        auth.hide = false;
        auth.disable = false;
        auth.permittedRoles = [];
      }
      setScreenDesign(sd, getScreenDesign(sd), ESdChangeReason.UpdateAuth);
    }
    changes.push(`allowAnonymousAccess -> ${normalizedInput.allowAnonymousAccess}`);
  }

  const authAfterAnonymous = getAuthorizationTarget(sd).authorization;
  if (
    normalizedInput.allowAllRoles !== undefined &&
    authAfterAnonymous.allowAllRoles !== normalizedInput.allowAllRoles
  ) {
    if (typeof sd?.allowAllRolesChanged === "function") {
      toggleAllowAllRoles(sd);
    } else {
      authAfterAnonymous.allowAllRoles = normalizedInput.allowAllRoles;
      authAfterAnonymous.allowAnonymousAccess = false;
      if (!normalizedInput.allowAllRoles) {
        authAfterAnonymous.hide = true;
        authAfterAnonymous.disable = false;
      } else {
        authAfterAnonymous.hide = false;
        authAfterAnonymous.disable = false;
        authAfterAnonymous.permittedRoles = [];
      }
      setScreenDesign(sd, getScreenDesign(sd), ESdChangeReason.UpdateAuth);
    }
    changes.push(`allowAllRoles -> ${normalizedInput.allowAllRoles}`);
  }

  const authAfterRoleMode = getAuthorizationTarget(sd).authorization;
  if (normalizedInput.isHidden !== undefined && authAfterRoleMode.hide !== normalizedInput.isHidden) {
    if (typeof sd?.isHiddenChanged === "function") {
      toggleIsHidden(sd);
    } else {
      authAfterRoleMode.hide = normalizedInput.isHidden;
      authAfterRoleMode.disable = !normalizedInput.isHidden;
      setScreenDesign(sd, getScreenDesign(sd), ESdChangeReason.UpdateAuth);
    }
    changes.push(`isHidden -> ${normalizedInput.isHidden}`);
  }

  const authAfterHidden = getAuthorizationTarget(sd).authorization;
  if (normalizedInput.isDisabled !== undefined && authAfterHidden.disable !== normalizedInput.isDisabled) {
    if (typeof sd?.isDisabledChanged === "function") {
      toggleIsDisabled(sd);
    } else {
      authAfterHidden.disable = normalizedInput.isDisabled;
      authAfterHidden.hide = !normalizedInput.isDisabled;
      setScreenDesign(sd, getScreenDesign(sd), ESdChangeReason.UpdateAuth);
    }
    changes.push(`isDisabled -> ${normalizedInput.isDisabled}`);
  }

  if (normalizedInput.roleIds !== undefined) {
    const syncResult = syncPermittedRoles(sd, getAuthorizationTarget(sd).authorization, normalizedInput.roleIds);
    changes.push(`permittedRoles set to ${normalizedInput.roleIds.length - syncResult.invalid.length} role(s)`);
    if (syncResult.invalid.length > 0) {
      changes.push(`skipped ${syncResult.invalid.length} invalid role ID(s): ${syncResult.invalid.join(", ")}`);
    }
  }

  if (changes.length === 0) {
    return { success: true, message: `No changes needed - ${getAuthorizationMessageScope(target)} already matches.` };
  }

  return {
    success: true,
    message: `Updated ${getAuthorizationMessageScope(target)}: ${changes.join("; ")}`
  };
}

// ─── Screen Role (project-level) ─────────────────────────────────────

const VALID_SCREEN_ROLES = [
  "none",
  "starting",
  "signin",
  "reset_password",
  "splash",
  "new_password",
  "verification",
  "offline",
  "document_upload_link"
] as const;

type ScreenRoleValue = (typeof VALID_SCREEN_ROLES)[number];

interface ISetScreenRoleSuccess {
  success: true;
  message: string;
  screenId: string;
  screenRole: string;
}

interface ISetScreenRoleError {
  error: string;
  validRoles?: readonly string[];
}

/**
 * Assign a project-level screen role (Starting Screen, Signin Screen, etc.) to a screen.
 * This sets project properties like `startingScreenId`, `signinScreenId`, etc.
 * Different from user/authorization roles — this controls which screen serves a system purpose.
 */
export async function setDesignerScreenRole(
  sdInstance: ScreenDesignInstance,
  input: { screenRole: string; screenId?: string }
): Promise<ISetScreenRoleSuccess | ISetScreenRoleError> {
  const role = input.screenRole?.toLowerCase()?.trim() as ScreenRoleValue;
  if (!VALID_SCREEN_ROLES.includes(role)) {
    return {
      error: `Invalid screenRole "${input.screenRole}". Must be one of: ${VALID_SCREEN_ROLES.join(", ")}`,
      validRoles: VALID_SCREEN_ROLES
    };
  }

  const sd = getValues(sdInstance);
  const screenId = input.screenId || sd.sdId;
  if (!screenId) {
    return { error: "No screenId provided and no current screen loaded." };
  }

  // Verify the screen exists
  const screenList = sd.screenList;
  if (screenList && !screenList.some((s: any) => s.id === screenId)) {
    return { error: `Screen with id "${screenId}" not found.` };
  }

  const project = _.cloneDeep(sd.project);

  // Clear this screen from any existing project-level role
  if (project.startingScreenId === screenId) project.startingScreenId = undefined;
  if (project.signinScreenId === screenId) project.signinScreenId = undefined;
  if (project.resetPasswordScreenId === screenId) project.resetPasswordScreenId = undefined;
  if (project.splashScreenId === screenId) project.splashScreenId = undefined;
  if (project.newPasswordScreenId === screenId) project.newPasswordScreenId = undefined;
  if (project.verificationCodeScreenId === screenId) project.verificationCodeScreenId = undefined;
  if (project.offlineScreenId === screenId) {
    project.offlineScreenId = undefined;
    sd.setScreenAsOfflineScreen?.(screenId, false);
  }
  if (project.documentUploadLinkScreenId === screenId) project.documentUploadLinkScreenId = undefined;

  // Assign the new role
  const roleLabels: Record<ScreenRoleValue, string> = {
    none: "None",
    starting: "Starting Screen",
    signin: "Signin Screen",
    reset_password: "Reset Password Screen",
    splash: "Splash Screen",
    new_password: "New Password Screen",
    verification: "Verification Screen",
    offline: "Offline Screen",
    document_upload_link: "Document Upload Link Screen"
  };

  switch (role) {
    case "starting":
      project.startingScreenId = screenId;
      break;
    case "signin":
      project.signinScreenId = screenId;
      break;
    case "reset_password":
      project.resetPasswordScreenId = screenId;
      break;
    case "splash":
      project.splashScreenId = screenId;
      break;
    case "new_password":
      project.newPasswordScreenId = screenId;
      break;
    case "verification":
      project.verificationCodeScreenId = screenId;
      break;
    case "offline":
      project.offlineScreenId = screenId;
      sd.setScreenAsOfflineScreen?.(screenId, true);
      break;
    case "document_upload_link":
      project.documentUploadLinkScreenId = screenId;
      break;
    case "none":
      // Already cleared above
      break;
  }

  await sd.updateProject(project);

  return {
    success: true,
    message:
      role === "none"
        ? `Cleared project-level screen role from screen "${screenId}".`
        : `Set screen "${screenId}" as ${roleLabels[role]}.`,
    screenId,
    screenRole: roleLabels[role]
  };
}
